import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, ArrowRight, Trash2 } from "lucide-react";

const columns = [
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "converted", label: "Converted" },
  { id: "lost", label: "Lost" },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const move = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Moved to ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const convert = async (id) => {
    try {
      await api.post(`/leads/${id}/convert`);
      toast.success("Converted to client");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete lead?")) return;
    await api.delete(`/leads/${id}`);
    load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="leads-page">
      <div className="mb-10">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Pipeline</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Leads.</h1>
      </div>

      <div className="kanban-scroll flex gap-5 overflow-x-auto pb-4" data-testid="kanban-board">
        {columns.map((col) => {
          const items = leads.filter((l) => (l.status || "new") === col.id);
          return (
            <div key={col.id} className="min-w-[300px] flex-shrink-0 bg-[#0A0A0A] border border-[#27272A] p-4" data-testid={`kanban-col-${col.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="font-display uppercase tracking-widest text-sm">{col.label}</div>
                <span className="text-xs text-zinc-500 bg-[#121212] px-2 py-0.5">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((l) => (
                  <div key={l.id} className="bg-[#121212] border border-[#27272A] p-3 hover:border-[#FF331F]/50 transition-colors" data-testid={`lead-card-${l.id}`}>
                    <div className="font-display uppercase text-sm mb-1">{l.name}</div>
                    <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white"><Mail className="h-3 w-3" /> {l.email}</a>
                    {l.phone && <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1"><Phone className="h-3 w-3" /> {l.phone}</div>}
                    {l.message && <p className="text-xs text-zinc-400 mt-2 line-clamp-3">{l.message}</p>}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#27272A]">
                      <select value={l.status || "new"} onChange={(e) => move(l.id, e.target.value)} className="bg-[#050505] border border-[#27272A] text-xs uppercase tracking-widest px-2 py-1 text-zinc-300" data-testid={`lead-status-${l.id}`}>
                        {columns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                      <div className="flex items-center gap-1">
                        {l.status !== "converted" && (
                          <button onClick={() => convert(l.id)} title="Convert" className="text-zinc-500 hover:text-[#FF331F]" data-testid={`convert-lead-${l.id}`}>
                            <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => remove(l.id)} className="text-zinc-500 hover:text-red-400" data-testid={`delete-lead-${l.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-zinc-600 text-center py-8">No leads</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
